import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, type Messaging } from 'firebase/messaging';
import { request, history } from 'umi';
import { stringify } from 'querystring';
import { showPushNotification } from '@/components/NotificationManager';
import { LS_TOKEN_NAME } from '@/constants';
import { handleImpersonationAuthFailure } from '@/utils/impersonation';

// Firebase configuration for dispatch-butler project
const firebaseConfig = {
  apiKey: "AIzaSyC3kStjJZC6FHzoJtdNIxM1io_RZI8WeBA",
  authDomain: "dispatch-butler.firebaseapp.com",
  projectId: "dispatch-butler",
  storageBucket: "dispatch-butler.firebasestorage.app",
  messagingSenderId: "562350431328",
  appId: "1:562350431328:web:31f8a9a28f58f3137c7705",
  measurementId: "G-48RL8CQF1D"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Cloud Messaging and get a reference to the service.
// FCM requires a secure context (HTTPS or localhost); on a plain-HTTP host
// getMessaging() throws, so guard it the same way the rest of this file
// already falls back to modal-only mode for unsupported browsers.
let messaging: Messaging | undefined;
try {
  messaging = getMessaging(app);
} catch (error) {
  console.warn('Firebase Messaging unavailable in this context:', error);
}

// Web push certificate key (VAPID key) - Generated from Firebase Console
// Firebase Console > Project Settings > Cloud Messaging > Web Push certificates > Generate key pair
const vapidKey = "BP3zQ_-MSSJlg7zOaIKaTinI6LDC68qDuGP81WahV5q8EBUun2ZE5wBgN34nFSdpY_GvihsHS5O3V06hotkTnlE";

/**
 * Request notification permission and get FCM token
 */
export const requestNotificationPermission = async (): Promise<string | null> => {
  try {
    console.log('🔔 Requesting notification permission...');

    // Request permission
    const permission = await Notification.requestPermission();
    console.log('🔔 Permission result:', permission);

    if (permission === 'granted') {
      console.log('✅ Notification permission granted, getting FCM token...');

      if (!messaging) {
        console.warn('❌ Firebase Messaging not available in this context');
        return null;
      }

      // Get the service worker registration we already created
      // @ts-ignore - PUBLIC_PATH is defined in config
      const publicPath = typeof PUBLIC_PATH !== 'undefined' ? PUBLIC_PATH : '/v2/';
      const swPath = `${publicPath}firebase-messaging-sw.js`;
      const registration = await navigator.serviceWorker.getRegistration(swPath);

      if (!registration) {
        console.error('❌ Service worker registration not found');
        return null;
      }

      // Get registration token using our existing service worker registration
      const token = await getToken(messaging, {
        vapidKey: vapidKey,
        serviceWorkerRegistration: registration
      });

      if (token) {
        console.log('✅ FCM Token received:', token.substring(0, 20) + '...');
        return token;
      } else {
        console.log('❌ No registration token available from Firebase');
        return null;
      }
    } else {
      console.log('❌ Permission denied or dismissed:', permission);
      return null;
    }
  } catch (error) {
    console.error('❌ Error occurred while retrieving token:', error);
    return null;
  }
};

/**
 * Redirect to login on auth failure, mirroring requestErrorConfig's 401 handler.
 * Used by the raw-fetch helpers in this file (which bypass umi's interceptors).
 */
const redirectToLogin = () => {
  if (handleImpersonationAuthFailure()) return;
  localStorage.setItem(LS_TOKEN_NAME, '');
  history.replace({
    pathname: '/user/login',
    search: stringify({ redirect: location.pathname + location.search }),
  });
};

/**
 * Validate existing FCM token with backend.
 *
 * Return shape:
 *  - valid: true                         → backend confirmed the token belongs to this user
 *  - valid: false, action: 'not_found'   → backend says the token isn't in the DB → re-register
 *  - valid: false, action: 'no_auth'     → caller already redirected to login; abort
 *  - valid: false, action: 'error'       → network/5xx; let caller surface a generic error
 *
 * NOTE: A 401 here means the user's session expired. Because EDB is logged-in-only,
 * we redirect to /user/login (same behavior as umi's errorHandler for request()) rather
 * than papering over it with a misleading "FCM token invalid" verdict.
 */
export const validateExistingToken = async (fcmToken: string): Promise<{valid: boolean, action: string, identity?: string}> => {
  try {
    const authToken = localStorage.getItem(LS_TOKEN_NAME);

    if (!authToken) {
      redirectToLogin();
      return {valid: false, action: 'no_auth'};
    }

    const response = await fetch('/api/twilio/validate_tenant_token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ fcm_token: fcmToken })
    });

    if (response.status === 401 || response.status === 403) {
      redirectToLogin();
      return {valid: false, action: 'no_auth'};
    }

    if (response.ok) {
      const data = await response.json();
      return {
        valid: data.token_valid,
        action: data.action_taken,
        identity: data.identity
      };
    }

    console.error('Token validation failed:', response.status);
    return {valid: false, action: 'error'};
  } catch (error) {
    console.error('Error validating token:', error);
    return {valid: false, action: 'error'};
  }
};

/**
 * Register FCM token with backend for tenant users
 */
export const registerTenantPushToken = async (token: string, sendTest: boolean = false): Promise<any> => {
  try {
    // Check if we have a valid JWT token before making the request
    const jwtToken = localStorage.getItem(LS_TOKEN_NAME);
    if (!jwtToken) {
      throw new Error('No authentication token found. Please log in again.');
    }

    const response = await request('/api/twilio/register_tenant_push_token', {
      method: 'POST',
      data: {
        fcm_token: token,
        send_test: sendTest,
        test_message: 'Test notification from tenant dashboard'
      },
      withToken: true
    });

    return response;
  } catch (error) {
    console.error('Error registering push token:', error);
    // Don't try to second-guess the error here. umi's errorHandler already redirects
    // to /user/login on real 401/403s. Anything else (e.g. a 400 "Invalid FCM token"
    // from Firebase rejecting a stale browser token) must NOT be reinterpreted as
    // "Authentication expired" — that string-matching bug is what produced the
    // misleading "token expired" message while the user was still fully logged in.
    throw error;
  }
};

/**
 * Setup foreground message listener
 */
export const setupForegroundMessageListener = (callback?: (payload: any) => void) => {
  if (!messaging) return;

  onMessage(messaging, (payload) => {
    console.log('Message received in foreground: ', payload);

    // Show custom notification modal
    if (payload.notification) {
      const notificationType = payload.data?.type;
      const validType = (notificationType === 'job_status' || notificationType === 'dispatch_update' || notificationType === 'status_change' || notificationType === 'route_change' || notificationType === 'appointment_change' || notificationType === 'tech_reassign' || notificationType === 'incoming_sms' || notificationType === 'general')
        ? (notificationType === 'status_change' ? 'dispatch_update' : notificationType)
        : 'general' as const;

      showPushNotification({
        title: payload.notification.title || 'New Notification',
        body: payload.notification.body || '',
        data: {
          ...payload.data,
          type: validType
        }
      });
    }

    if (callback) {
      callback(payload);
    }
  });
};

/**
 * Register Firebase messaging service worker
 */
export const registerFirebaseServiceWorker = async (): Promise<void> => {
  if ('serviceWorker' in navigator) {
    try {
      // Use the correct public path for the service worker
      // @ts-ignore - PUBLIC_PATH is defined in config
      const publicPath = typeof PUBLIC_PATH !== 'undefined' ? PUBLIC_PATH : '/v2/';
      const swPath = `${publicPath}firebase-messaging-sw.js`;

      console.log('🔧 Registering Firebase service worker at:', swPath);

      // Check if already registered
      const existingRegistration = await navigator.serviceWorker.getRegistration(swPath);
      if (existingRegistration) {
        console.log('✅ Firebase service worker already registered:', existingRegistration);
        return;
      }

      const registration = await navigator.serviceWorker.register(swPath);
      console.log('✅ Firebase service worker registered:', registration);

      // Wait for the service worker to be ready
      const sw = registration.installing;
      if (sw) {
        await new Promise((resolve) => {
          sw.addEventListener('statechange', () => {
            if (sw.state === 'activated') {
              resolve(void 0);
            }
          });
        });
      }
    } catch (error) {
      console.error('❌ Firebase service worker registration failed:', error);
      throw error;
    }
  }
};

/**
 * Initialize push notifications for tenant users
 */
export const initializeTenantPushNotifications = async (
  onMessageReceived?: (payload: any) => void,
  sendTestNotification: boolean = false
): Promise<{ success: boolean; token?: string; error?: string; mode?: 'full' | 'modal-only' }> => {
  try {
    // Always setup the foreground message listener for our custom modal
    if (onMessageReceived) {
      setupForegroundMessageListener(onMessageReceived);
    } else {
      setupForegroundMessageListener(); // Use default modal system
    }

    // Check if browser supports notifications
    if (!('Notification' in window)) {
      // Browser doesn't support notifications - use modal-only mode
      return {
        success: true,
        mode: 'modal-only',
        error: 'Browser notifications not supported, using modal-only mode'
      };
    }

    // Check if service worker is supported
    if (!('serviceWorker' in navigator)) {
      // No service worker support - use modal-only mode
      return {
        success: true,
        mode: 'modal-only',
        error: 'Service workers not supported, using modal-only mode'
      };
    }

    // Register Firebase messaging service worker FIRST
    await registerFirebaseServiceWorker();

    // Wait a moment for the service worker to be fully active
    await new Promise(resolve => setTimeout(resolve, 100));

    // Check if we have an existing token in localStorage
    const existingToken = localStorage.getItem('fcm_token');

    if (existingToken && Notification.permission === 'granted') {
      console.log('Found existing FCM token, validating...');

      // Validate existing token with backend
      const validation = await validateExistingToken(existingToken);

      if (validation.valid) {
        console.log('Existing token is valid, using it');
        return {
          success: true,
          mode: 'full',
          token: existingToken
        };
      } else if (validation.action === 'no_auth') {
        // Session expired — validateExistingToken already redirected to login. Bail out.
        return { success: false, error: 'Session expired' };
      } else {
        console.log('Existing token is invalid, removing and getting new one. Action:', validation.action);
        localStorage.removeItem('fcm_token');
      }
    }

    // Request permission and get new token (either no existing token or existing was invalid)
    console.log('🔑 Requesting new FCM token...');
    const token = await requestNotificationPermission();
    console.log('🔑 Received token:', token ? token.substring(0, 20) + '...' : 'None');

    if (!token) {
      // Permission denied or failed - fallback to modal-only mode
      console.log('❌ No token received, falling back to modal-only mode');
      return {
        success: true,
        mode: 'modal-only',
        error: 'Browser notification permission denied, using modal-only mode'
      };
    }

    // Store token in localStorage for status checking
    console.log('💾 Storing token in localStorage...');
    localStorage.setItem('fcm_token', token);

    // Verify it was stored
    const storedToken = localStorage.getItem('fcm_token');
    console.log('✅ Token stored verification:', storedToken ? storedToken.substring(0, 20) + '...' : 'Failed to store');

    // Register token with backend
    console.log('📤 Registering token with backend...');
    const registrationResult = await registerTenantPushToken(token, sendTestNotification);
    console.log('📤 Registration result:', registrationResult);

    return {
      success: true,
      mode: 'full',
      token: token,
      ...registrationResult
    };

  } catch (error) {
    console.error('Error initializing push notifications:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Simulate a server-side notification for testing modal-only mode
 * This can be used when browser notifications are not available
 */
export const simulateServerNotification = (title: string, body: string, data?: any) => {
  // Simulate the payload structure that would come from Firebase
  const payload = {
    notification: {
      title,
      body
    },
    data: data || {}
  };

  // Trigger the same handler that Firebase would call
  setupForegroundMessageListener();

  // Manually trigger the notification display
  if (payload.notification) {
    const notificationType = payload.data?.type;
    const validType = (notificationType === 'job_status' || notificationType === 'dispatch_update' || notificationType === 'status_change' || notificationType === 'route_change' || notificationType === 'appointment_change' || notificationType === 'tech_reassign' || notificationType === 'incoming_sms' || notificationType === 'general')
      ? (notificationType === 'status_change' ? 'dispatch_update' : notificationType)
      : 'general' as const;

    showPushNotification({
      title: payload.notification.title || 'New Notification',
      body: payload.notification.body || '',
      data: {
        ...payload.data,
        type: validType
      }
    });
  }
};

export { messaging };
